/**
 * Every external system the app can render, keyed by the string stored in
 * `collection.provider_key`.
 *
 * Adding one is an entry here and a module beside it. Nothing else in the app
 * -- not the loader, not the sidebar, not the create dialog -- names a provider
 * directly.
 */

import { ExternalProviderModule } from "./types";
import { gcalendar } from "./gcalendar";
import { gmail } from "./gmail";

export const providerModules: Record<string, ExternalProviderModule> = {
  [gmail.key]: gmail,
  [gcalendar.key]: gcalendar,
};

/** In the order the create-collection library offers them. */
export const providerLibrary: ExternalProviderModule[] =
  Object.values(providerModules);

export function getProvider(
  key: string | null | undefined,
): ExternalProviderModule | null {
  if (!key) return null;
  return providerModules[key] ?? null;
}

/**
 * Which systems a set of provider keys needs a grant on. A collection and its
 * subcollections share one, which is why connecting is a whole-tree action.
 */
export function systemsFor(keys: string[]): string[] {
  const systems: string[] = [];
  for (const key of keys) {
    const provider = getProvider(key);
    if (provider && !systems.includes(provider.systemId)) {
      systems.push(provider.systemId);
    }
  }
  return systems;
}

/**
 * Whether a provider's records join the all-collections timeline.
 *
 * Off by default, and deliberately so: a mailbox is dense enough to bury a
 * space's own records under it, and "everything I have written" is the question
 * that page answers. Turning it on is a per-provider decision, made on the root
 * collection's settings.
 *
 * Stored in the root's `provider_config` rather than as a column, because it
 * means nothing to the server -- which is the same reason `provider_config`
 * exists at all.
 */
export const SHOW_IN_ALL = "show_in_all";

export function showsInAllCollections(
  config: Record<string, unknown> | null | undefined,
): boolean {
  return config?.[SHOW_IN_ALL] === true;
}
